import * as mediasoupClient from 'mediasoup-client';
import logger from './logger';

const BITRATE_0 = 300000;
const BITRATE_1 = 1000000;
const BITRATE_2 = 3000000;

// Possibly change that this to an internal function later on
export async function getIceServers(socket) {
  if (process.env.NODE_ENV === 'development') {
    return [
      {
        urls: `turn:${window.__TURN_ADDR__}:${window.__TURN_PORT__}?transport=udp`,
        username: window.__TURN_USER__,
        credential: window.__TURN_PASS__,
      },
    ];
  }

  let iceServers = await socket.request('getIceServers');

  iceServers = iceServers.map((server) => ({
    urls: server.url,
    ...server,
  }));

  delete iceServers.url;

  return iceServers;
}

export async function getUserMedia({ audio = true, video = true }) {
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video,
      audio,
    });
  } catch (err) {
    logger.log('Error getting streams', { err });
  }
  return stream;
}

export async function requestPermissions() {
  let devices = await navigator.mediaDevices.enumerateDevices();
  if (devices.every((d) => !d.label)) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      } else {
        return false;
      }
    } catch (err) {
      logger.log(err.message);
      return false;
    }
  }
  return true;
}

export async function setupDevice(roomId, socket) {
  const routerRtpCapabilities = await socket.request(
    'getRouterRtpCapabilities',
    roomId
  );
  if (!routerRtpCapabilities) return null;

  const device = new mediasoupClient.Device({
    iceServers: await getIceServers(socket),
  });
  await device.load({ routerRtpCapabilities });
  return device;
}

export async function setupProducerTransport(roomId, device, socket) {
  const producerData = await socket.request('createProducerTransport', roomId, {
    rtpCapabilities: device.rtpCapabilities,
  });

  logger.log('producerData', producerData);

  const transport = window.__ENABLE_TURN__
    ? device.createSendTransport({
        ...producerData,
        iceServers: await getIceServers(socket),
        iceTransportPolicy: 'relay',
      })
    : device.createSendTransport(producerData);

  transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
    logger.log('producer transport connect');
    socket
      .request('connectProducerTransport', roomId, {
        transportId: transport.id,
        dtlsParameters,
      })
      .then(callback)
      .catch(errback);
  });

  transport.on(
    'produce',
    async ({ kind, rtpParameters, appData }, callback, errback) => {
      logger.log('producer transport produce');
      const { id } = await socket.request('produce', roomId, {
        transportId: transport.id,
        kind,
        rtpParameters,
        appData,
      });
      logger.log('producer transport produced', id);
      callback({ id });
    }
  );

  transport.on('connectionstatechange', (state) => {
    logger.log('produce state changed', state);
  });
  return transport;
}

export async function startProducer(
  { videoTrack, audioTrack },
  producerTransport,
  device
) {
  let videoProducer;
  let audioProducer;
  const ps = [];

  const firstVideoCodec = device.rtpCapabilities.codecs.find(
    (c) => c.kind === 'video'
  );

  const isAndroid = /(android)/i.test(navigator.userAgent);
  const vp9 = firstVideoCodec.mimeType.toLowerCase().includes('vp9');
  if (vp9) {
    logger.log('using vp9');
  }

  // Chromium on Android has an issue when combining captureStream and vp8
  //  We cannot use scaleResolutionDownBy on android
  //  See https://bugs.chromium.org/p/chromium/issues/detail?id=1359707
  if (videoTrack) {
    logger.log('send video produce');
    const p = producerTransport
      .produce({
        track: videoTrack,
        encodings: vp9
          ? [{ scalabilityMode: 'L3T3_KEY', maxBitrate: BITRATE_0 }]
          : isAndroid
          ? [
              { maxBitrate: BITRATE_0, scalabilityMode: 'L1T2' },
              { maxBitrate: BITRATE_1, scalabilityMode: 'L1T2' },
              { maxBitrate: BITRATE_2, scalabilityMode: 'L1T2' },
            ]
          : [
              {
                scaleResolutionDownBy: 4,
                maxBitrate: BITRATE_0,
                scalabilityMode: 'L1T2',
              },
              {
                scaleResolutionDownBy: 2,
                maxBitrate: BITRATE_1,
                scalabilityMode: 'L1T2',
              },
              {
                scaleResolutionDownBy: 1,
                maxBitrate: BITRATE_2,
                scalabilityMode: 'L1T2',
              },
            ],
        codecOptions: {
          videoGoogleStartBitrate: 1000,
        },
        disableTrackOnPause: false,
        //zeroRtpOnPause: true, // REMOVE ME only for debugging data transfers
      })
      .then((producer) => {
        videoProducer = producer;
        videoProducer.pause();
        logger.log('video produce done', videoProducer.id);
      });
    ps.push(p);
  }

  // AUDIO
  if (audioTrack) {
    logger.log('send audio produce');
    const p = producerTransport
      .produce({
        track: audioTrack,
        disableTrackOnPause: true,
        codecOptions: {
          opusStereo: 0,
          opusDtx: 0, // TODO we want this to be 1, but for recording it needs to be 0, otherwise ffmpeg shrinks audio on silence
        },
      })
      .then((producer) => {
        audioProducer = producer;
        logger.log('audio produce done', audioProducer.id);
      });
    ps.push(p);
  }

  await Promise.all(ps);

  return { videoProducer, audioProducer };
}

export async function startVideoProducer(stream, producerTransport, device) {
  const videotracks = stream.getVideoTracks();
  if (!videotracks.length) return {};
  const videoTrack = videotracks[0];
  const producer = await startProducer(
    { videoTrack },
    producerTransport,
    device
  );

  return producer.videoProducer;
}

export async function startAudioProducer(stream, producerTransport, device) {
  const audiotracks = stream.getAudioTracks();
  if (!audiotracks.length) return undefined;
  const audioTrack = audiotracks[0];
  const producer = await startProducer(
    { audioTrack },
    producerTransport,
    device
  );

  return producer.audioProducer;
}

const matchError = (e, name, message) =>
  e.name === name && e.message === message;
const noop = () => {};

export function handleGetUserMediaError(
  e,
  {
    onSystemRejection = noop,
    onUserDenied = noop,
    onUserDismissed = noop,
    onCameraTaken = noop,
    catchAll = noop,
  } = {}
) {
  if (!(e instanceof Error)) return;

  const isCameraInUse = () => {
    // Additional heuristic: Camera might be in use if the error message contains specific keywords
    return /Device in use|Could not start video source/.test(e.message);
  };

  if (
    /* system rejection, macOS */
    /* Chrome */ matchError(
      e,
      'NotAllowedError',
      'Permission denied by system'
    ) ||
    /* Edge */ matchError(e, 'NotAllowedError', 'Permission denied') ||
    /* Firefox */ matchError(
      e,
      'NotFoundError',
      'The object can not be found here'
    )
  ) {
    onSystemRejection();
  } else if (
    /* system rejection, Windows */
    /* Chrome */ matchError(
      e,
      'NotReadableError',
      'Could not start video source'
    ) ||
    /* Edge */ matchError(
      e,
      'NotReadableError',
      'Could not start video source'
    ) ||
    /* Firefox */ matchError(
      e,
      'NotReadableError',
      'Failed to allocate videosource'
    )
  ) {
    if (isCameraInUse()) {
      onCameraTaken();
    } else {
      onSystemRejection();
    }
  } else if (
    /* user denied, macOS & Windows */
    /* Chrome */ matchError(e, 'NotAllowedError', 'Permission denied') ||
    /* Safari */ matchError(
      e,
      'NotAllowedError',
      'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.'
    ) ||
    /* Edge */ matchError(e, 'NotAllowedError', 'Permission denied') ||
    /* Firefox */ matchError(
      e,
      'NotAllowedError',
      'The request is not allowed by the user agent or the platform in the current context.'
    )
  ) {
    onUserDenied();
  } else if (
    /* camera taken, any OS */
    /* Chrome */ matchError(e, 'NotReadableError', 'Device in use') ||
    /* Firefox */ matchError(e, 'AbortError', 'Starting videoinput failed') ||
    /* Chrome */ matchError(e, 'Error', 'Could not start video source')
  ) {
    onCameraTaken();
  } else if (e.message === 'Permission dismissed') {
    onUserDismissed();
  } else if (e.message === 'Requested device not found') {
    noop();
  } else {
    catchAll();
  }
}
